import { MemberDTO, NoteDTO } from '../services/apiClient';
import { NoteTypeConstants } from '../constants/NoteTypeConstants';
import { ComponentConstants } from '../constants/ComponentConstants';

export interface MemberNoteIcon {
    type: 'payment' | 'disciplinary' | 'flag' | 'tag';
    color: string;
    count?: number;
}

/**
 * Maps table context to the corresponding component ID
 * @param tableContext - The table context string
 * @returns The component ID GUID for the table
 */
const getComponentIdForTable = (tableContext: string): string => {
    const componentMap: Record<string, string> = {
        members: ComponentConstants.MEMBERS,
        activities: ComponentConstants.ACTIVITIES,
        scores: ComponentConstants.SCORES,
        endorsements: ComponentConstants.ENDORSEMENTS,
        courses: ComponentConstants.DEDICATED_COURSES,
        rpl: ComponentConstants.RPL,
    };

    return componentMap[tableContext.toLowerCase()] || '';
};

/**
 * Determines which note icons should be displayed for a member in a table context
 * @param member - The member DTO containing notes and payment info
 * @param tableContext - The table context (members, activities, courses, endorsements, rpl, scores)
 * @param recordId - The ID of the specific record being displayed (for filtering flags and tags)
 * @returns Array of icon configurations to display
 */
export const getMemberNoteIcons = (
    member: MemberDTO | undefined | null,
    tableContext: string,
    recordId?: string
): MemberNoteIcon[] => {
    if (!member) return [];

    const icons: MemberNoteIcon[] = [];

    // 1. Payment Due - Check if paidUpTo date is today or past
    if (member.paidUpTo) {
        const paidUpToDate = new Date(member.paidUpTo);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (paidUpToDate <= today) {
            icons.push({
                type: 'payment',
                color: '#ef4444', // red-500
            });
        }
    }

    // Get unresolved notes for the member
    const unresolvedNotes = member.memberNotes || [];

    // 2. Disciplinary - Hand icon with color from note
    const disciplinaryNotes = unresolvedNotes.filter(
        (note) => note.noteTypeId === NoteTypeConstants.DISCIPLINARY
    );

    if (disciplinaryNotes.length > 0) {
        const note = disciplinaryNotes[0];
        // Use custom color from note, fallback to noteType colorGrading, fallback to orange
        const color = note.colour || note.noteType?.colourGrading?.colour || '#f97316'; // orange-500

        icons.push({
            type: 'disciplinary',
            color: color,
        });
    }

    // 3. Flag - Tiered by count (1=teal, 2=orange, 3+=red)
    const flagNotes = unresolvedNotes.filter(
        (note) => note.noteTypeId?.toLowerCase() === NoteTypeConstants.FLAG
    );

    if (flagNotes.length > 0) {
        let flagColor = '#14b8a6'; // teal-500 (1 flag)

        if (flagNotes.length === 2) {
            flagColor = '#f97316'; // orange-500
        } else if (flagNotes.length >= 3) {
            flagColor = '#ef4444'; // red-500
        }

        icons.push({
            type: 'flag',
            color: flagColor,
            count: flagNotes.length,
        });
    }

    // 4. Tag - Record-specific icon with color from note (filtered by recordId only)
    const tagNotes = unresolvedNotes.filter(
        (note) => note.noteTypeId?.toLowerCase() === NoteTypeConstants.TAG &&
            (!recordId || note.recordId?.toLowerCase() === recordId.toLowerCase())
    );

    if (tagNotes.length > 0) {
        const note = tagNotes[0];
        // Use custom color from note, fallback to noteType colorGrading, fallback to blue
        const color = note.colour || note.noteType?.colourGrading?.colour || '#3b82f6'; // blue-500

        icons.push({
            type: 'tag',
            color: color,
        });
    }

    return icons;
};

/**
 * Get the table-specific tag icon name for lucide-react
 * @param tableContext - The table context
 * @returns The lucide icon name to use for tags in this table
 */
export const getTagIconForTable = (tableContext: string): string => {
    const iconMap: Record<string, string> = {
        members: 'User',
        scores: 'Target',
        activities: 'List',
        endorsements: 'ThumbsUp',
        courses: 'Book',
        rpl: 'Award',
    };

    return iconMap[tableContext.toLowerCase()] || 'Tag';
};
